import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { SvgIconComponent } from 'angular-svg-icon';
import { format } from 'date-fns';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import {
  NzFormItemComponent,
  NzFormLabelComponent,
  NzFormModule,
} from 'ng-zorro-antd/form';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { Observable, of, Subject, take, takeUntil } from 'rxjs';
import { DRAWER_IDS } from '../../../../../../core/constants/drawer-ids.constant';
import { LookupItem } from '../../../../../../core/models/lookup.models';
import { DrawerService } from '../../../../../../core/services/drawer.service';
import { LoaderService } from '../../../../../../core/services/loader.service';
import { CommonService } from '../../../../../../shared/services/common.service';
import { DataLogService } from '../data-log.service';
import {
  DataLogFilterDrawerParams,
  DataLogFilterFormValues,
  TouchedUser,
} from '../data-log.interface';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';

@Component({
  selector: 'app-data-log-filter',
  standalone: true,
  imports: [
    NzFormModule,
    NzFormItemComponent,
    NzFormLabelComponent,
    NzSelectModule,
    NzDatePickerModule,
    SvgIconComponent,
    NzButtonModule,
    ReactiveFormsModule,
    NzSpinModule,
    CommonModule,
    FormsModule,
    NzToolTipModule,
  ],
  templateUrl: './data-log-filter.component.html',
  styleUrl: './data-log-filter.component.scss',
})
export class DataLogFilterComponent implements OnInit, OnDestroy {
  dataLogsFilterForm!: FormGroup;
  auditTypeList: LookupItem[] = [];
  loading$: Observable<boolean> = of(false);
  userList: TouchedUser[] = [];
  private readonly destroy$ = new Subject<void>();
  UnitTypeList: { id: number; value: string }[] = [
    { id: 1, value: 'Frequency/Duration Based Datasheet' },
    { id: 2, value: 'Latency' },
    { id: 3, value: 'Partial Interval' },
    { id: 4, value: 'Replacement Behavior' },
  ];

  constructor(
    private readonly fb: FormBuilder,
    private readonly commonService: CommonService,
    private readonly loaderService: LoaderService,
    private readonly drawerService: DrawerService,
    private readonly dataLogService: DataLogService
  ) {
    this.dataLogsFilterForm = this.fb.group({
      dataAuditTypeId: [null],
      dataUnitType: [null],
      userId: [null],
      startDataDateRange: [null],
      endDataDateRange: [null],
      startDateRange: [null],
      endDateRange: [null],
    });
    this.drawerService
      .getParams<DataLogFilterDrawerParams>(DRAWER_IDS.DATA_LOGS_FILTER)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(params => {
        if (params?.appliedFilters) {
          this.dataLogsFilterForm.patchValue(params.appliedFilters);
        }
      });
  }

  ngOnInit(): void {
    this.getMasterData();
    // Fetch users for the current case (async)
    this.drawerService
      .getParams<DataLogFilterDrawerParams>(DRAWER_IDS.DATA_LOGS_FILTER)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(params => {
        // Try to get caseId from params or fallback to 0
        let caseId = 0;
        if (params?.caseId) {
          caseId = params.caseId || 0;
        }
        if (caseId) {
          this.dataLogService.getUsersTouchedCase(caseId).subscribe(res => {
            this.userList = res.result || [];
          });
        }
      });
  }
  validateDates(ctrl1: string, ctrl2: string) {
    const startDate = this.dataLogsFilterForm.get(ctrl1)?.value;
    const endDate = this.dataLogsFilterForm.get(ctrl2)?.value;

    // Ensure both dates are valid before comparing
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);

      if (start > end) {
        // Set errors on both fields
        this.dataLogsFilterForm.get(ctrl1)?.setErrors({ dateInvalid: true });
        this.dataLogsFilterForm.get(ctrl2)?.setErrors({ dateInvalid: true });
      } else {
        // Clear only `dateInvalid` errors if they exist
        this.dataLogsFilterForm.get(ctrl1)?.setErrors(null);
        this.dataLogsFilterForm.get(ctrl2)?.setErrors(null);
      }
    }
  }

  convertIsoToMinutes(isoString: string): number | null {
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return null; // Invalid date
    return date.getHours() * 60 + date.getMinutes(); // Convert to total minutes
  }

  async getMasterData() {
    this.loaderService.show();
    await Promise.all([
      this.commonService.getDataByType('DataAuditType').toPromise(),
    ])
      .then(([auditTypeList]) => {
        this.auditTypeList = auditTypeList?.result ? auditTypeList.result : [];
      })
      .catch(e => console.error(e))
      .finally(() => this.loaderService.hide());
  }

  reset() {
    this.dataLogsFilterForm.reset();
    const filterValues = this.dataLogsFilterForm.value;
    const appliedFilterLength = this.calculateAppliedFilterLength(filterValues);
    this.drawerService.setPersistentState(DRAWER_IDS.DATA_LOGS_FILTER, {
      appliedFilters: filterValues,
      appliedFilterLength: appliedFilterLength,
    });
    const drawerRef = this.drawerService.getDrawerRef(
      DRAWER_IDS.DATA_LOGS_FILTER
    );
    drawerRef?.close({ filterValues });
  }

  apply() {
    const filterValues = this.dataLogsFilterForm.value;
    if (filterValues.startDataDateRange) {
      filterValues.startDataDateRange = format(
        filterValues.startDataDateRange,
        'yyyy-MM-dd'
      );
    }
    if (filterValues.endDataDateRange) {
      filterValues.endDataDateRange = format(
        filterValues.endDataDateRange,
        'yyyy-MM-dd'
      );
    }
    if (filterValues.startDateRange) {
      filterValues.startDateRange = format(
        filterValues.startDateRange,
        'yyyy-MM-dd'
      );
    }
    if (filterValues.endDateRange) {
      filterValues.endDateRange = format(
        filterValues.endDateRange,
        'yyyy-MM-dd'
      );
    }
    const appliedFilterLength = this.calculateAppliedFilterLength(filterValues);
    this.drawerService.setPersistentState(DRAWER_IDS.DATA_LOGS_FILTER, {
      appliedFilters: filterValues,
      appliedFilterLength: appliedFilterLength,
    });
    const drawerRef = this.drawerService.getDrawerRef(
      DRAWER_IDS.DATA_LOGS_FILTER
    );
    drawerRef?.close({ filterValues });
  }

  private calculateAppliedFilterLength(
    values: DataLogFilterFormValues
  ): number {
    return Object.values(values).filter(
      value => value !== null && value !== undefined && value !== ''
    ).length;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
